import React, { useEffect, useMemo, useState } from 'react';
import styles from './Avatar.module.css';

export function getAvatarInitials(name) {
  const value = String(name || '').trim();
  if (!value) return '?';

  const parts = value.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

export function Avatar({
  src,
  name,
  initials,
  alt = '',
  size,
  className = '',
  imgClassName = '',
  fallbackClassName = '',
  style,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  ariaLabel,
  title,
  loading,
  draggable,
}) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const resolvedSize = Number(size);
  const sizedStyle = useMemo(() => {
    if (!Number.isFinite(resolvedSize) || resolvedSize <= 0) return style;

    return {
      '--avatar-size': `${resolvedSize}px`,
      '--avatar-font-size': `${Math.max(10, Math.round(resolvedSize * 0.34))}px`,
      width: `${resolvedSize}px`,
      height: `${resolvedSize}px`,
      ...style,
    };
  }, [resolvedSize, style]);

  const hasImage = Boolean(src) && !failed;
  const displayInitials = initials || getAvatarInitials(name || alt);
  const rootClassName = [
    styles.root,
    onClick ? styles.clickable : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={rootClassName}
      style={sizedStyle}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={role}
      tabIndex={tabIndex}
      aria-label={ariaLabel || ((role || onClick) ? (alt || name || title) : undefined)}
      title={title}
    >
      {hasImage ? (
        <img
          className={[styles.img, imgClassName].filter(Boolean).join(' ')}
          src={src}
          alt={alt || name || ''}
          loading={loading}
          draggable={draggable}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className={[styles.fallback, fallbackClassName].filter(Boolean).join(' ')}>
          {displayInitials}
        </span>
      )}
    </div>
  );
}
